import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authToken, setAuthToken] = useState(null);
  const [error, setError] = useState(null);

  // Auto-logout after 60 minutes of inactivity
  const INACTIVITY_TIMEOUT = 60 * 60 * 1000; // 60 minutes in milliseconds
  const WARNING_TIMEOUT = 50 * 60 * 1000; // Show warning at 50 minutes (10 min before logout)
  const inactivityTimerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const [showInactivityWarning, setShowInactivityWarning] = useState(false);

  // Tax residency and location tracking
  const [showTaxResidencyModal, setShowTaxResidencyModal] = useState(false);
  const [showLocationChangeModal, setShowLocationChangeModal] = useState(false);
  const [detectedCountry, setDetectedCountry] = useState(null);
  const [locationCheckData, setLocationCheckData] = useState(null);
  const [taxResidencyConfirmed, setTaxResidencyConfirmed] = useState(false); // Local flag to prevent modal reopening

  // Initialize auth token from localStorage
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    console.log('[AuthProvider] Initializing, token exists:', !!token);
    if (token) {
      setAuthToken(token);
      checkAuth(token);
    } else {
      console.log('[AuthProvider] No token found, user not authenticated');
      setLoading(false);
    }
  }, []);

  // Setup inactivity tracking when user is authenticated
  useEffect(() => {
    // Only setup timer when user logs in/out (user.id changes)
    // NOT on every user property change (prevents unnecessary resets)
    if (!user?.id) {
      // Clear timers if user logs out
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
      if (warningTimerRef.current) {
        clearTimeout(warningTimerRef.current);
        warningTimerRef.current = null;
      }
      setShowInactivityWarning(false);
      return;
    }

    console.log('[InactivityTimer] Initializing for user:', user.id);

    // Function to reset inactivity timer
    const resetInactivityTimer = () => {
      // Clear existing timers
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
      if (warningTimerRef.current) {
        clearTimeout(warningTimerRef.current);
        warningTimerRef.current = null;
      }

      // Hide warning if shown
      setShowInactivityWarning(false);

      // Set warning timer (10 minutes before logout)
      warningTimerRef.current = setTimeout(() => {
        console.log('[InactivityTimer] User will be logged out in 10 minutes due to inactivity');
        setShowInactivityWarning(true);
      }, WARNING_TIMEOUT);

      // Set logout timer (60 minutes)
      inactivityTimerRef.current = setTimeout(() => {
        console.log('[InactivityTimer] User inactive for 60 minutes - logging out');
        setShowInactivityWarning(false);
        logout();
      }, INACTIVITY_TIMEOUT);
    };

    // Activity events to track
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, resetInactivityTimer, { passive: true });
    });

    // Initialize timer
    resetInactivityTimer();

    // Cleanup function
    return () => {
      console.log('[InactivityTimer] Cleaning up for user:', user.id);
      
      // Clear timers
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
      if (warningTimerRef.current) {
        clearTimeout(warningTimerRef.current);
        warningTimerRef.current = null;
      }
      
      // Remove event listeners
      events.forEach(event => {
        document.removeEventListener(event, resetInactivityTimer, { passive: true });
      });
    };
  }, [user?.id]); // Only re-run when user ID changes (login/logout)

  // Function to extend session (user acknowledges warning)
  const extendSession = () => {
    setShowInactivityWarning(false);
    // This will trigger a reset of all timers through the activity
    document.dispatchEvent(new Event('mousedown'));
  };

  const checkAuth = async (token) => {
    console.log('[AuthProvider] Checking authentication with token...');
    // Set loading to true at start to prevent race conditions
    setLoading(true);
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const userData = response.data;
      
      // Set user BEFORE setting loading to false
      // This prevents ProtectedRoute from seeing isAuthenticated=false
      setUser(userData);
      console.log('[AuthProvider] ✓ User authenticated:', userData.email);

      // Check if user needs to confirm tax residency (first time)
      // Use local flag to prevent reopening after confirmation
      if (!userData.tax_residency_confirmed && !taxResidencyConfirmed) {
        setDetectedCountry(userData.country);
        setShowTaxResidencyModal(true);
      } else {
        // Check for location changes (returning users)
        checkLocationChange(token);
      }
      
      // Set loading false AFTER user is set
      setLoading(false);
    } catch (error) {
      console.error('[AuthProvider] ❌ Auth check failed:', error.message);
      console.error('[AuthProvider] Status:', error.response?.status);
      console.error('[AuthProvider] Clearing invalid token from localStorage');
      localStorage.removeItem('auth_token');
      setAuthToken(null);
      setUser(null);
      setLoading(false);
    }
  };

  const checkLocationChange = async (token) => {
    try {
      const response = await axios.get(`${API}/country/check-location-change`, {
        headers: { Authorization: `Bearer ${token || authToken}` }
      });
      const data = response.data;
      
      if (data.location_changed) {
        setLocationCheckData(data);
        setShowLocationChangeModal(true);
      }
    } catch (error) {
      console.error('Location check failed:', error);
    }
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, {
      email,
      password,
      name
    });
    // New behavior: registration doesn't return token, requires email verification
    return response.data;
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, {
      email,
      password
    });
    const token = response.data.access_token;
    localStorage.setItem('auth_token', token);
    setAuthToken(token);
    await checkAuth(token);
    return response.data;
  };

  const resendVerification = async (email) => {
    const response = await axios.post(`${API}/auth/resend-verification`, null, {
      params: { email }
    });
    return response.data;
  };

  const loginWithGoogle = async () => {
    try {
      // NEW: Call backend to get Google OAuth URL
      const response = await axios.get(`${API}/auth/google/login`);
      const authUrl = response.data.auth_url;
      
      console.log('[GoogleAuth] Redirecting to Google OAuth:', authUrl);
      
      // Redirect to Google OAuth
      window.location.href = authUrl;
    } catch (error) {
      console.error('[GoogleAuth] Failed to initiate Google login:', error);
      setError('Failed to connect to Google. Please try again.');
    }
  };

  const handleGoogleCallback = async () => {
    // NEW: Get authorization code from URL query params (standard OAuth2)
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const error = urlParams.get('error');

    console.log('[GoogleAuth] Callback URL:', window.location.href);
    console.log('[GoogleAuth] Authorization code:', code ? 'present' : 'missing');

    // Check for OAuth errors
    if (error) {
      console.error('[GoogleAuth] ❌ OAuth error:', error);
      setError(`Google login failed: ${error}`);
      setLoading(false);
      return false;
    }

    if (!code) {
      console.error('[GoogleAuth] ❌ No authorization code found in URL');
      console.error('[GoogleAuth] Expected format: /auth/callback?code=xxx');
      setLoading(false);
      return false;
    }

    try {
      console.log('[GoogleAuth] ✓ Authorization code found, exchanging for token...');
      
      // NEW: Exchange code for user data via backend
      const response = await axios.post(`${API}/auth/google/callback`, {
        code: code
      });
      
      console.log('[GoogleAuth] ✓ Backend response received');
      
      const token = response.data.access_token;
      const userData = response.data.user;
      const isNewUser = response.data.is_new_user; // Backend should return this flag
      
      if (!token || !userData) {
        console.error('[GoogleAuth] ❌ Missing token or user data in response');
        console.error('[GoogleAuth] Response:', JSON.stringify(response.data));
        setLoading(false);
        return false;
      }
      
      console.log('[GoogleAuth] ✓ Token received for user:', userData.email);
      console.log('[GoogleAuth] Is new user:', isNewUser);
      
      // Store token and set basic user data immediately
      localStorage.setItem('auth_token', token);
      setAuthToken(token);
      setUser(userData);
      
      // Store new user flag for onboarding
      if (isNewUser) {
        localStorage.setItem('show_onboarding', 'true');
      }
      
      console.log('[GoogleAuth] ✓ Token stored in localStorage');
      console.log('[GoogleAuth] ✓ User state set:', userData.email);
      
      // Clean URL immediately for better UX
      window.history.replaceState({}, document.title, window.location.pathname);
      console.log('[GoogleAuth] ✓ URL cleaned');
      
      // Fetch full user profile with tax residency check (async, non-blocking)
      // This runs in background after successful login
      try {
        console.log('[GoogleAuth] Loading full profile...');
        const profileResponse = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const fullUserData = profileResponse.data;
        setUser(fullUserData);
        console.log('[GoogleAuth] ✓ Full profile loaded');
        
        // Check tax residency if needed
        if (!fullUserData.tax_residency_confirmed && !taxResidencyConfirmed) {
          setDetectedCountry(fullUserData.country);
          setShowTaxResidencyModal(true);
          console.log('[GoogleAuth] Tax residency modal will be shown');
        } else {
          checkLocationChange(token);
        }
      } catch (profileError) {
        console.warn('[GoogleAuth] ⚠️ Could not load full profile, using basic data:', profileError);
        // Continue anyway - basic login successful
      }
      
      setLoading(false);
      console.log('[GoogleAuth] ✅ Authentication complete and successful!');
      return true;
      
    } catch (error) {
      console.error('[GoogleAuth] ❌ Authentication failed during session exchange');
      console.error('[GoogleAuth] Error:', error.message);
      console.error('[GoogleAuth] Error response:', error.response?.data);
      console.error('[GoogleAuth] Error status:', error.response?.status);
      
      // Clear any partial state
      localStorage.removeItem('auth_token');
      setAuthToken(null);
      setUser(null);
      setLoading(false);
      return false;
    }
  };

  const logout = async () => {
    try {
      if (authToken) {
        await axios.post(`${API}/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${authToken}` }
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('auth_token');
      setAuthToken(null);
      setUser(null);
    }
  };

  // Handle tax residency confirmation
  const handleTaxResidencyConfirm = (country) => {
    console.log('[AuthContext] handleTaxResidencyConfirm called with country:', country);
    
    // Set local flag to prevent modal from reopening
    setTaxResidencyConfirmed(true);
    
    // Close modal immediately
    setShowTaxResidencyModal(false);
    console.log('[AuthContext] Modal closed');
    
    // Update user state
    setUser(prev => ({
      ...prev,
      country,
      tax_residency_confirmed: true
    }));
    
    // Refresh user data from backend asynchronously
    setTimeout(async () => {
      try {
        const token = localStorage.getItem('auth_token');
        if (token) {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
          console.log('[AuthContext] User data refreshed from backend');
        }
      } catch (error) {
        console.error('[AuthContext] Failed to refresh user data:', error);
      }
    }, 1000);
  };

  // Handle location change update
  const handleLocationUpdate = (country) => {
    setShowLocationChangeModal(false);
    // Update user state
    setUser(prev => ({
      ...prev,
      country
    }));
  };

  // Handle location change dismiss
  const handleLocationChangeDismiss = () => {
    setShowLocationChangeModal(false);
  };

  const value = {
    user,
    authToken,
    loading,
    error,
    setError,
    register,
    login,
    loginWithGoogle,
    handleGoogleCallback,
    logout,
    resendVerification,
    isAuthenticated: !!user,
    showInactivityWarning,
    extendSession,
    showTaxResidencyModal,
    showLocationChangeModal,
    detectedCountry,
    locationCheckData,
    handleTaxResidencyConfirm,
    handleLocationUpdate,
    handleLocationChangeDismiss,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
